'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { geistMonoFont } from '@/_style/fonts';
import { cn } from '@/lib/utils';

interface LegalTableOfContentsProps {
	sections: Array<{ id: string; title: string }>;
	mobile?: boolean;
}

export default function LegalTableOfContents({ sections, mobile = false }: LegalTableOfContentsProps) {
	const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');
	const [isOpen, setIsOpen] = useState(false);
	const observerRef = useRef<IntersectionObserver | null>(null);

	useEffect(() => {
		// Landing layout scrolls inside <main>, not the document viewport
		const scrollRoot = document.querySelector('main');

		observerRef.current = new IntersectionObserver(
			(entries) => {
				const visibleEntries = entries.filter((e) => e.isIntersecting);
				if (visibleEntries.length > 0) {
					// Pick the topmost visible section
					const topmost = visibleEntries.reduce((a, b) =>
						a.boundingClientRect.top < b.boundingClientRect.top ? a : b
					);
					setActiveId(topmost.target.id);
				}
			},
			{
				root: scrollRoot,
				rootMargin: '-80px 0px -60% 0px',
				threshold: 0
			}
		);

		sections.forEach(({ id }) => {
			const el = document.getElementById(id);
			if (el) observerRef.current?.observe(el);
		});

		return () => observerRef.current?.disconnect();
	}, [sections]);

	const handleClick = useCallback((id: string) => {
		const el = document.getElementById(id);
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			setActiveId(id);
			setIsOpen(false);
		}
	}, []);

	if (mobile) {
		return (
			<div className='border-border rounded-lg border'>
				<button
					onClick={() => setIsOpen(!isOpen)}
					className='text-foreground flex w-full items-center justify-between px-4 py-3 text-sm font-medium'
					aria-expanded={isOpen}
				>
					<span className={geistMonoFont.className}>Table of Contents</span>
					<ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
				</button>
				{isOpen && (
					<nav
						aria-label='Table of contents'
						className='border-border border-t px-4 py-3'
					>
						<ol className='space-y-1.5'>
							{sections.map(({ id, title }) => (
								<li key={id}>
									<button
										onClick={() => handleClick(id)}
										className={cn(
											'w-full py-1 text-left text-sm transition-colors',
											activeId === id
												? 'font-medium text-pink-600 dark:text-pink-400'
												: 'text-muted-foreground hover:text-foreground'
										)}
									>
										{title}
									</button>
								</li>
							))}
						</ol>
					</nav>
				)}
			</div>
		);
	}

	return (
		<nav
			aria-label='Table of contents'
			className='sticky top-20'
		>
			<p
				className={`${geistMonoFont.className} text-muted-foreground mb-4 text-xs font-medium tracking-wider uppercase`}
			>
				On this page
			</p>
			<ol className='border-border/50 space-y-1 border-l'>
				{sections.map(({ id, title }) => (
					<li key={id}>
						<button
							onClick={() => handleClick(id)}
							className={cn(
								'-ml-px block w-full border-l-2 py-1.5 pl-4 text-left text-sm transition-colors',
								activeId === id
									? 'border-pink-500 font-medium text-pink-600 dark:text-pink-400'
									: 'text-muted-foreground hover:border-border hover:text-foreground border-transparent'
							)}
						>
							{title}
						</button>
					</li>
				))}
			</ol>
		</nav>
	);
}
